import { useRef, useState } from "react";
import { formatAmount, formatUsd } from "../lib/swap-quote";
import { matches } from "../hooks";
import { Modal } from "./Modal";
import { TokenLogo } from "./TokenLogo";

/** A catalog token with the member's balance joined on, as the app's picker lists it. */
export interface PickerToken {
  key: string;
  chainId: number;
  chainName: string;
  symbol: string;
  address: string;
  decimals: number;
  logoUrl: string | null;
  /** Human decimal, "0" when not held. */
  balance: string;
  /** USD per token from the member's holding, or null (no dollar entry then). */
  price: number | null;
  usdValue: number;
}

export interface TokenPickerProps {
  title: string;
  tokens: PickerToken[];
  selectedKey: string;
  /** Symbols on more than one chain; only those rows name their network. */
  ambiguous: Set<string>;
  onPick: (key: string) => void;
  onClose: () => void;
}

/**
 * The app's token sheet as a console modal: the member's holdings first, each
 * row with its balance and dollar value, and a search over the symbol. It sits
 * over the page like `ConfirmDialog` and closes the same ways (Escape, a click
 * outside, the close button, and on a phone a pull down on the sheet).
 */
export function TokenPicker({
  title,
  tokens,
  selectedKey,
  ambiguous,
  onPick,
  onClose,
}: TokenPickerProps) {
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  // Not on a touch screen: focusing the search there raises the keyboard over
  // the very list the picker was opened to choose from. The field is one tap
  // away for the member who has more tokens than fit.
  const [focusSearch] = useState(() => !matches("(pointer: coarse)"));

  const q = query.trim().toLowerCase();
  const rows = q ? tokens.filter((t) => t.symbol.toLowerCase().includes(q)) : tokens;

  return (
    <Modal
      labelledBy="token-picker-title"
      className="token-picker"
      initialFocus={focusSearch ? searchRef : undefined}
      onClose={onClose}
    >
      {(close) => (
        <>
          <div className="token-picker-head">
            <h2 id="token-picker-title">{title}</h2>
            <button type="button" className="link-button" onClick={close}>
              Close
            </button>
          </div>
          <input
            ref={searchRef}
            type="search"
            aria-label="Search tokens"
            placeholder="Search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <ul className="token-list" role="listbox" aria-label={title}>
            {rows.length === 0 ? <li className="subtle token-list-empty">No match</li> : null}
            {rows.map((t) => {
              const held = Number(t.balance) > 0;
              return (
                <li key={t.key} role="option" aria-selected={t.key === selectedKey}>
                  <button
                    type="button"
                    className="token-row"
                    aria-label={t.symbol}
                    onClick={() => onPick(t.key)}
                  >
                    <TokenLogo url={t.logoUrl} symbol={t.symbol} />
                    <span className="token-row-name">
                      {t.symbol}
                      {ambiguous.has(t.symbol) ? <span className="subtle"> {t.chainName}</span> : null}
                    </span>
                    <span className="token-row-balance">
                      <span>{held ? formatAmount(t.balance) : "0"}</span>
                      {held ? <span className="subtle">{formatUsd(t.usdValue)}</span> : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Modal>
  );
}
